import posthog from "posthog-js";
import { IS_LOADING, IS_SAVING, LEAVE_ADDED, LEAVE_UPDATED, GET_LEAVES } from "./leaveActionsTypes";
import { axiosBasic, getUserInfo } from "../../../../../api/axios";
import { config } from "../../../../../api/endPoints";
import { Common } from "../../../../../utils";
import { ACCRUAL_START_DATE_VALUES } from "../../../../../utils/constants";
const { LeaveType, LeaveAccrual } = config.REST_API;

export const isLoading = (value) => ({
  type: IS_LOADING,
  payload: value,
});

export const isSaving = (value) => ({
  type: IS_SAVING,
  payload: value,
});

export const onAddLeaves = (values) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const isDeductible = values?.takeFromAllowance || values?.deducted || false;
  const enableAccrual = isDeductible ? (values?.enableAccrual || false) : false;
  const userInput = {
    hasName: !!(values?.name),
    hasColor: !!(values?.color),
    deducted: isDeductible,
    requiresApproval: !!(values?.requiresApproval),
    active: values?.active !== false,
    enableAccrual,
  };
  // If Allowance is disabled (non-deductible), disable accrual
  const isDeductibleInner = values.takeFromAllowance || values.deducted || false;
  const enableAccrualInner = isDeductibleInner ? (values.enableAccrual || false) : false;

  const obj = {
    name: values.name,
    color: values.color,
    deducted: isDeductibleInner,
    isMaxAbsent: values.isMaxAbsent === true || values.isMaxAbsent === 'true' || false,
    maxAbsent: values.maxAbsent !== undefined && values.maxAbsent !== null ? values.maxAbsent : 0,
    requiresApproval: values.requiresApproval || false,
    defaultLeaveCount: values.defaultLeaveCount || 0,
    active: values.active !== false,
    icon: values.icon || values.selectOption || "no-icon",
    // Accrual fields - only set if Allowance is enabled
    // Special case: If frequency is "Annually", always send it even if enableAccrual is false
    accrualRate: enableAccrualInner ? (values.accrualRate || 0) : 0,
    accrualFrequency: (values.accrualFrequency === 'Annually')
      ? 'Annually'
      : (enableAccrualInner ? (values.accrualFrequency || null) : null),
    accrualCap: enableAccrualInner ? (values.accrualCap !== undefined ? values.accrualCap : (values.maxAccrualBalance !== undefined ? values.maxAccrualBalance : 0)) : 0,
    // For Annually, set proration to false and don't send accrual start type
    enableProration: (values.accrualFrequency === 'Annually')
      ? false
      : (enableAccrualInner ? (values.enableProration === true) : false),
    accrualStartType: (values.accrualFrequency === 'Annually')
      ? null
      : (enableAccrualInner ? (values.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START) : ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START),
    tieredRates: enableAccrualInner ? (values.tieredRates || []) : [],
    enableAccrual: enableAccrualInner,
    // Division fields for Accrual Rate and Accrual Cap
    // C# enum LeaveDivision: Days = 0, Hours = 1
    // values.accrualRateDivision comes from saveToServer as integer (0 or 1), use it directly
    accrualRateDivision: (typeof values.accrualRateDivision === 'number')
      ? values.accrualRateDivision  // Use integer value directly (0 or 1)
      : ((typeof values.accrualRateDivision === 'string' && (values.accrualRateDivision === 'Hours' || values.accrualRateDivision.toLowerCase() === 'hours'))
        ? 1  // Hours
        : 0), // Days
    accrualCapDivision: (typeof values.accrualCapDivision === 'number')
      ? values.accrualCapDivision  // Use integer value directly (0 or 1)
      : ((typeof values.accrualCapDivision === 'string' && (values.accrualCapDivision === 'Hours' || values.accrualCapDivision.toLowerCase() === 'hours'))
        ? 1  // Hours
        : 0)  // Days
  };

  dispatch(isSaving(true));
  axiosBasic
    .post(LeaveType.CreateLeaveType, obj)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onAddLeaves", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        // Merge response data with sent data to ensure all fields (especially accrual) are preserved
        const mergedData = data ? { ...obj, ...data } : obj;
        dispatch({ type: LEAVE_ADDED, payload: mergedData });
        Common.showSuccessMessage(message || 'Leave type has been added successfully.');
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onAddLeaves", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to add leave type');
    })
    .finally(() => {
      dispatch(isSaving(false));
    });
};

export const onUpdateLeaves = (values) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {
    leaveTypeId: values?.id,
    hasName: !!(values?.name),
    hasColor: !!(values?.color),
    deducted: !!(values?.takeFromAllowance || values?.deducted),
    requiresApproval: !!(values?.requiresApproval),
    active: values?.active !== false,
  };
  dispatch(isLoading(true));

  // If Allowance is disabled (non-deductible), disable accrual
  const isDeductible = values.takeFromAllowance || values.deducted || false;
  const enableAccrual = isDeductible ? (values.enableAccrual || false) : false;

  const obj = {
    id: values.id,
    color: values.color,
    name: values.name,
    deducted: isDeductible,
    isMaxAbsent: values.isMaxAbsent === true || values.isMaxAbsent === 'true' || false,
    maxAbsent: values.maxAbsent !== undefined && values.maxAbsent !== null ? values.maxAbsent : 0,
    requiresApproval: values.requiresApproval || false,
    defaultLeaveCount: values.defaultLeaveCount || 0,
    active: values.active !== false,
    icon: values.icon || values.selectOption || "no-icon",
    // Accrual fields - only set if Allowance is enabled
    // Special case: If frequency is "Annually", always send it even if enableAccrual is false
    accrualCap: enableAccrual ? (values.accrualCap !== undefined ? values.accrualCap : (values.maxAccrualBalance !== undefined ? values.maxAccrualBalance : 0)) : 0,
    // For Annually, set proration to false and don't send accrual start type
    enableProration: (values.accrualFrequency === 'Annually')
      ? false
      : (enableAccrual ? (values.enableProration === true) : false),
    tieredRates: enableAccrual ? (values.tieredRates || []) : [],
    accrualRate: enableAccrual ? (values.accrualRate || 0) : 0,
    accrualFrequency: (values.accrualFrequency === 'Annually')
      ? 'Annually'
      : (enableAccrual ? (values.accrualFrequency || null) : null),
    accrualStartType: (values.accrualFrequency === 'Annually')
      ? null
      : (enableAccrual ? (values.accrualStartType || ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START) : ACCRUAL_START_DATE_VALUES.LEAVE_YEAR_START),
    enableAccrual: enableAccrual,
    // Division fields for Accrual Rate and Accrual Cap
    // C# enum LeaveDivision: Days = 0, Hours = 1
    // values.accrualRateDivision comes from saveToServer as integer (0 or 1), use it directly
    accrualRateDivision: (typeof values.accrualRateDivision === 'number')
      ? values.accrualRateDivision  // Use integer value directly (0 or 1)
      : ((typeof values.accrualRateDivision === 'string' && (values.accrualRateDivision === 'Hours' || values.accrualRateDivision.toLowerCase() === 'hours'))
        ? 1  // Hours
        : 0), // Days
    accrualCapDivision: (typeof values.accrualCapDivision === 'number')
      ? values.accrualCapDivision  // Use integer value directly (0 or 1)
      : ((typeof values.accrualCapDivision === 'string' && (values.accrualCapDivision === 'Hours' || values.accrualCapDivision.toLowerCase() === 'hours'))
        ? 1  // Hours
        : 0)  // Days
  };

  axiosBasic
    .put(LeaveType.CreateLeaveType, obj)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateLeaves", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage('Leave type has been updated successfully');
        const mergedData = data ? { ...obj, ...data } : obj;
        dispatch({ type: LEAVE_UPDATED, payload: mergedData });
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateLeaves", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to update leave type');
    })
    .finally(() => {
      dispatch(isSaving(false));
    });
};

export const onSoftRemoveLeave = (id) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { leaveTypeId: id };
  axiosBasic
    .delete(LeaveType.SoftRemove + id)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSoftRemoveLeave", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message);
      } else {
        Common.showErrorMessage(message);
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onSoftRemoveLeave", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error);
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const onGetLeaveTypes = () => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = {};
  dispatch(isLoading(true));
  axiosBasic
    .get(LeaveType.GetLeaveTypesOfCompany)
    .then((response) => {
      const { success, data, total } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetLeaveTypes", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : response.data?.message,
        });
      }
      if (success) {
        // Map accrualTiers to tieredRates for each leave type
        const mappedData = (data || []).map(leave => ({
          ...leave,
          tieredRates: leave.accrualTiers ? leave.accrualTiers.map(tier => ({
            id: tier.id,
            years: tier.years || 0,
            days: tier.days || 0,
            hours: tier.hours || 0,
            tierOrder: tier.tierOrder || 0,
            active: tier.active !== undefined ? tier.active : true
          })) : (leave.tieredRates || [])
        }))
          .sort((a, b) => {
            // Sort by active status first (active first)
            if (a.active !== b.active) {
              return a.active ? -1 : 1;
            }
            // Then sort by name alphabetically
            return (a.name || '').localeCompare(b.name || '');
          });
        dispatch(showLeaves(mappedData));
      } else {
        dispatch(showLeaves([]));
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onGetLeaveTypes", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error);
      dispatch(showLeaves([]));
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const showLeaves = (leaves) => ({
  type: GET_LEAVES,
  payload: leaves,
});

// Tier CRUD Operations
export const onCreateTier = (leaveTypeId, tierData) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { leaveTypeId, hasTierData: !!tierData };
  dispatch(isSaving(true));
  const obj = {
    LeaveTypeId: leaveTypeId,
    Years: tierData.years || 0,
    Days: tierData.days || 0,
    Hours: tierData.hours || 0,
    TierOrder: tierData.tierOrder || 0,
    Active: tierData.active !== undefined ? tierData.active : true
  };

  return axiosBasic
    .post(LeaveAccrual.CreateTier, obj)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onCreateTier", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || 'Tier created successfully');
        return { success: true, data };
      } else {
        Common.showErrorMessage(message || 'Failed to create tier');
        return { success: false, error: message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onCreateTier", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to create tier');
      return { success: false, error: error?.response?.data?.message || 'Failed to create tier' };
    })
    .finally(() => {
      dispatch(isLoading(false));
    });
};

export const onUpdateTier = (tierId, tierData) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { tierId, hasTierData: !!tierData };
  dispatch(isSaving(true));
  const obj = {
    Years: tierData.years || 0,
    Days: tierData.days || 0,
    Hours: tierData.hours || 0,
    TierOrder: tierData.tierOrder || 0,
    Active: tierData.active !== undefined ? tierData.active : true
  };

  return axiosBasic
    .put(LeaveAccrual.UpdateTier + tierId, obj)
    .then((response) => {
      const { success, message, data } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateTier", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        // Don't show success message for every update to avoid spam
        // Common.showSuccessMessage(message || 'Tier updated successfully');
        return { success: true, data };
      } else {
        // Only show error if it's not a "not found" error (which we handle gracefully)
        if (!message?.toLowerCase().includes('not found')) {
          Common.showErrorMessage(message || 'Failed to update tier');
        }
        return { success: false, error: message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onUpdateTier", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      const errorMessage = error?.response?.data?.message || 'Failed to update tier';
      // Only show error if it's not a "not found" error
      if (!errorMessage.toLowerCase().includes('not found')) {
        Common.showErrorMessage(errorMessage);
      }
      return { success: false, error: errorMessage };
    })
    .finally(() => {
      dispatch(isSaving(false));
    });
};

export const onDeleteTier = (tierId) => (dispatch) => {
  const userId = getUserInfo()?.selfUserId ?? null;
  const userInput = { tierId };
  dispatch(isLoading(true));

  return axiosBasic
    .delete(LeaveAccrual.DeleteTier + tierId)
    .then((response) => {
      const { success, message } = response.data;
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteTier", {
          type: success ? "info" : "exception",
          userId,
          success,
          userInput,
          reason: success ? undefined : message,
        });
      }
      if (success) {
        Common.showSuccessMessage(message || 'Tier deleted successfully');
        return { success: true };
      } else {
        Common.showErrorMessage(message || 'Failed to delete tier');
        return { success: false, error: message };
      }
    })
    .catch((error) => {
      if (typeof posthog !== "undefined" && posthog.capture) {
        posthog.capture("onDeleteTier", {
          type: "exception",
          userId,
          success: false,
          userInput,
          reason: error?.response?.data?.message ?? error?.message ?? "Request failed",
        });
      }
      Common.showErrorMessage(error?.response?.data?.message || 'Failed to delete tier');
      return { success: false, error: error?.response?.data?.message || 'Failed to delete tier' };
    })
    .finally(() => {
      dispatch(isSaving(false));
    });
};